/**
 * One access case on the list, as a compact tile (5 Oct, product owner's UAT).
 *
 * WHY (5 Oct, product owner's UAT): the list used to show every item with
 * its buttons, so a case took a screen and nobody could see where it stood.
 * Now three short lines: who, the type, the request and the stage; who it
 * waits on (or, in the archive, its approvals counted, for auditors); and the
 * items in words, open first and done muted, with the start or leaving date.
 * No buttons here: the whole tile opens the case page, which has them all.
 *
 * 7 Oct (UAT walkthrough): the coloured dots needed a legend nobody had, so
 * they are words now ("Open: 365FO, CRM · Done: Account"); and the generic
 * "Next steps open" stage is not shown, since the progress line and the
 * "Action for you" pill already say it.
 */

import { Badge, Card, Typography } from "@norican/react";
import { Link } from "react-router";

import { useDateFormatter } from "@/components/common";
import { startDateValue } from "@/features/starters";
import { useT } from "@/i18n";
import type { AccessCase } from "@/lib/api";

import { accessCasePath, itemProgress } from "../lib/access";
import { JourneyBar } from "./journey-bar";
import { StatusBadge } from "./status-badge";
import { useWaitingWords } from "./waiting-on";

const SUMMARY_KEYS = ["approved", "rejected", "pre_approved", "waiting"] as const;

export interface AccessCaseCardProps {
  item: AccessCase;
}

export function AccessCaseCard({ item }: AccessCaseCardProps) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const words = useWaitingWords();
  const items = item.items ?? [];
  const actions = item.action_count ?? 0;
  const archived = item.archived === true;
  const stage = item.stage ?? "in_progress";
  const start = startDateValue(item.start_date);
  // Leave (6 Oct): the leaving date where a starter shows the start date.
  const leaving = startDateValue(item.leaving_date);

  /** "Approvals: 2 approved, 1 rejected, 1 pre-approved · approved by …" */
  function approvalsLine(): string {
    const summary = item.approvals_summary;
    const parts = SUMMARY_KEYS.filter((key) => (summary?.[key] ?? 0) > 0).map((key) =>
      t(`access.summary.${key}`, { n: summary?.[key] ?? 0 }),
    );
    if (parts.length === 0) {
      return t("access.summary.none");
    }
    const line = t("access.summary.label", { list: parts.join(", ") });
    const by = summary?.approved_by ?? [];
    return by.length === 0 ? line : `${line} · ${t("access.summary.by", { names: by.join(", ") })}`;
  }

  const progress = itemProgress(items, words.shortName);
  const dates = [
    start !== null ? t("access.startDate", { date: formatDate(start) }) : null,
    leaving !== null ? t("access.leavingDate", { date: formatDate(leaving) }) : null,
  ].filter((text): text is string => text !== null);

  // 19 Oct: approvals past their reminders, rejections nobody followed up,
  // in words rather than more badges (21 Oct: the "not on the HR list" flag is retired).
  const flags = [
    (item.overdue_approvals ?? 0) > 0
      ? t("access.overdueCount", { n: item.overdue_approvals ?? 0 })
      : null,
    (item.open_rejections ?? 0) > 0
      ? t("access.rejectedCount", { n: item.open_rejections ?? 0 })
      : null,
  ].filter((text): text is string => text !== null);

  const second = archived ? approvalsLine() : words.caseLine(item.waiting_on ?? []);

  return (
    <Card
      variant="outline"
      padding="sm"
      accent={actions > 0 ? "left" : undefined}
      accentTone={actions > 0 ? "accent" : undefined}
      className="ease-standard transition hover:shadow-md"
    >
      <Link to={accessCasePath(item.case_id)} className="flex min-w-0 flex-col gap-1 rounded-md">
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <Typography as="span" variant="heading-6" className="min-w-0 break-words">
            {item.person_name}
          </Typography>
          <Typography as="span" variant="body-sm" className="text-fg-muted">
            {`${t(`access.type.${item.type}`)} · ${t("access.caseNumber", { number: item.case_number })}`}
          </Typography>
          {/* 19 Oct (UI review, finding 3): two badges at most, the stage and
              what is asked of the reader; the counts are words below. */}
          {stage !== "next_steps" ? (
            <StatusBadge domain="stage" status={stage}>
              {t(`access.stage.${stage}`)}
            </StatusBadge>
          ) : null}
          {actions > 0 ? (
            <Badge tone="accent" variant="solid" size="xs">
              {t("access.actionForYouCount", { n: actions })}
            </Badge>
          ) : null}
        </span>
        {/* 29 Oct: where the case is on its road, at a glance. */}
        {(item.journey ?? []).length > 0 ? (
          <JourneyBar steps={item.journey ?? []} variant="dots" />
        ) : null}
        {flags.length > 0 ? (
          <Typography as="span" variant="body-sm" className="text-danger-fg min-w-0 break-words">
            {flags.join(" · ")}
          </Typography>
        ) : null}
        {second ? (
          <Typography
            as="span"
            variant="body-sm"
            tone={archived ? "muted" : undefined}
            className="line-clamp-2 min-w-0 break-words"
          >
            {second}
          </Typography>
        ) : null}
        {items.length > 0 || dates.length > 0 ? (
          <Typography as="span" variant="caption" className="min-w-0 break-words">
            {[
              progress.open.length > 0 ? (
                <span key="open">
                  {t("access.progress.open", { list: progress.open.join(", ") })}
                </span>
              ) : null,
              progress.rejected.length > 0 ? (
                <span key="rejected" className="text-danger-fg">
                  {t("access.progress.rejected", { list: progress.rejected.join(", ") })}
                </span>
              ) : null,
              progress.done.length > 0 ? (
                <span key="done" className="text-fg-muted">
                  {t("access.progress.done", { list: progress.done.join(", ") })}
                </span>
              ) : null,
              ...dates.map((text) => (
                <span key={text} className="text-fg-muted">
                  {text}
                </span>
              )),
            ]
              .filter((part) => part !== null)
              .flatMap((part, index) =>
                index === 0
                  ? [part]
                  : [
                      <span key={`sep-${index}`} aria-hidden="true" className="text-fg-muted">
                        {" · "}
                      </span>,
                      part,
                    ],
              )}
          </Typography>
        ) : null}
      </Link>
    </Card>
  );
}
